import * as $ from 'jquery';
import { disposableSubscription } from 'common/barrels/core';
import { Observable, Subject } from 'common/barrels/rx';
import * as array from 'common/functions/array';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import { TableCellContainer } from 'runtime/controls/tablecellcontainer';
import * as rxui from 'common/rxui/core';

import 'runtime/styles/controls/tablecontainer.less';

export class RuleBinding {
    constructor (public isElementVisible: string[]) { }
}

export class DimensionMetadata {
    constructor(public size: number, public isVisible: boolean, public sizeType: string, public ruleBindings: RuleBinding, public uniqueId: string) { }
}
    
export class DimensionVisibility {
    constructor(public index: number, public value: boolean) { }
}

export class TableContainer extends ContainerControl {
    private rowMetadata: DimensionMetadata[];
    private columnMetadata: DimensionMetadata[];

    private columnElements: JQuery[];
    private rowElements: JQuery[];

    private rowVisibilityUpdates = new Subject<DimensionVisibility>();
    private columnVisibilityUpdates = new Subject<DimensionVisibility>();

    private rowVisibility: boolean[];
    private columnVisibility: boolean[];        

    private updateRequired = new Subject<{}>();

    private layoutUpdated = new Subject<{}>();

    constructor(args: ControlArgs) {
        super(args);

        this.methods.register(this.config.elementName, this);

        this.$element.addClass('control-container tablecontainer');

        var $cellContainers = this.$element.children();
        this.bindElements($cellContainers,this, this.fieldContext, this.methods);

        this.columnMetadata = <DimensionMetadata[]>this.config.tableDefinition.columns;
        this.columnElements = this.identifyElements(this.columnMetadata, $cellContainers, x => x.columnIndex());
        this.rowMetadata = <DimensionMetadata[]>this.config.tableDefinition.rows;
        this.rowElements = this.identifyElements(this.rowMetadata, $cellContainers, x => x.rowIndex());

        var cells = $cellContainers.toArray().map<TableCellContainer>(x => $(x).data('control'));

        this.columnVisibility = this.columnMetadata.map(x => x.isVisible);
        this.rowVisibility = this.rowMetadata.map(x => x.isVisible);

        cells.forEach(x => x.cellVisible(this.columnVisibility[x.columnIndex()] && this.rowVisibility[x.rowIndex()]));

        this.subscriptions = disposableSubscription(
            rxui.redrawRequired(this.updateRequired, this.container.activatedStream, this.visibilityStream, this.stretches() ? this.container.resizeStream() : Observable.never()).subscribe(() => this.updateLayout()),

            this.columnVisibilityUpdates.subscribe(x => {
                if (this.columnVisibility[x.index] !== x.value) {
                    this.columnVisibility[x.index] = x.value;
                    cells.filter(y => y.columnIndex() === x.index).forEach(y => y.cellVisible(this.columnVisibility[x.index] && this.rowVisibility[y.rowIndex()]));
                    this.updateRequired.next(({}));
                }
            }),
            this.rowVisibilityUpdates.subscribe(x => {
                if (this.rowVisibility[x.index] !== x.value) {
                    this.rowVisibility[x.index] = x.value;
                    cells.filter(y => y.rowIndex() === x.index).forEach(y => y.cellVisible(this.columnVisibility[y.columnIndex()] && this.rowVisibility[x.index]));
                    this.updateRequired.next(({}));
                }
            }),
            this.bindDimensionVisibility(this.columnMetadata, this.columnVisibilityUpdates),
            this.bindDimensionVisibility(this.rowMetadata, this.rowVisibilityUpdates),

            this.processBindings(this.config)
        );

        this.applyTooltip(this.$element);

        window.setTimeout(() => this.updateRequired.next(({})), 0);

        this.initialize();
    }

    private identifyElements(metadata: DimensionMetadata[], $cellContainers: JQuery, dimensionIdentifier: (x: TableCellContainer) => number) {
        return metadata.map((x, i) => 
            $cellContainers
                .toArray()
                .map(x => $(x))
                .map(y => ({ $control: y, control: <TableCellContainer>y.data('control') }))
                .filter(y => dimensionIdentifier(y.control) === i)
                .map(y => y.$control)
                .reduce((a, y) => a.add(y), $())
            );
    }

    private updateLayout() {
        this.updateLayoutDimension(this.columnMetadata, this.columnElements, x => x.data('control').rightEdge(), (x, y) => x.css('left', y), (x, y) => x.width(y), this.$element.width(), this.columnVisibility, x => this.$element.width(x), this.config.elementAlignment.horizontal);
        this.updateLayoutDimension(this.rowMetadata, this.rowElements, x => x.data('control').bottomEdge(), (x, y) => x.css('top', y), (x, y) => x.height(y), this.$element.height(), this.rowVisibility, x => this.$element.height(x), this.config.elementAlignment.vertical);
        this.layoutUpdated.next({});
    }

    private updateLayoutDimension(metadata: DimensionMetadata[], elements: JQuery[], edgeGetter: (x: JQuery) => number,
        positionSetter: (x: JQuery, y: number) => void, sizeSetter: (x: JQuery, y: number) => void, availableSpace: number, vis: boolean[],
        controlSizeSetter: (x: number) => void,
        dimensionAlignment: string
    ) {

        var numbered = metadata.map((x, i) => ({ index: i, meta: x }));

        var participating = this.config.collapseHiddenSections ? numbered.filter(x => vis[x.index]) : numbered;
        if (this.config.collapseHiddenSections)
            numbered.filter(x => !vis[x.index]).forEach(x => elements[x.index].hide());

        var fixed = participating.filter(x => x.meta.sizeType === 'pixel').map(x => ({ index: x.index, size: x.meta.size }));

        var auto = participating
            .filter(x => x.meta.sizeType === 'auto')
            .map(x => ({ index: x.index, size: array.maxDefaultZero(elements[x.index].children(':visible').toArray().map(y => edgeGetter($(y)))) }));

        var starSpace = availableSpace - array.sum(fixed.map(x => x.size)) - array.sum(auto.map(x => x.size));
        var rawStar = participating.filter(x => x.meta.sizeType === 'star').map(x => ({ index: x.index, stars: x.meta.size }));
        var starTotal = array.sum(rawStar.map(x => x.stars));
        var star = rawStar.map(x => ({ index: x.index, size: Math.max(starSpace * x.stars / starTotal, 0) }));

        var totalSize = fixed.concat(auto).concat(star).sort((x, y) => x.index - y.index).reduce((a, x) => {
            var $selector = elements[x.index];
            positionSetter($selector, a);
            sizeSetter($selector, x.size);
            return a + x.size;
        }, 0);

        if (this.config.matchContentSize && dimensionAlignment !== 'stretch')
            controlSizeSetter(totalSize);
    }

    private bindDimensionVisibility(metadata: DimensionMetadata[], vis: Subject<DimensionVisibility>) {
        return disposableSubscription(...metadata
            .map((x, i) => ({ meta: x, index: i }))
            .filter(x => x.meta.ruleBindings.isElementVisible !== undefined)
            .reduce((a, x) => a.concat(x.meta.ruleBindings.isElementVisible.map(y => ({ meta: x.meta, index: x.index, binding: y, defaultValue: x.meta.isVisible }))), [])
            .map(x => this.values.bindOneWay('Rule', this.fieldContext, x.binding, y => this.applyDimensionVisibility(vis, x.index, y, x.defaultValue)))
        );
    }

    private applyDimensionVisibility(vis: Subject<DimensionVisibility>, index: number, value: boolean, defaultValue: boolean) {
        vis.next(new DimensionVisibility(index, value !== null ? value : defaultValue));
    }

    focus() { }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToChildElements(this.$element, this.writableAgnosticDerivedEnabledState());
    }

    resizeStream(): Observable<{}> {
        return this.layoutUpdated;
    }
}

registerBinding('tablecontainer', TableContainer);